import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  filterMovies,
  getMoviesByFlag,
  paginate,
  sortMovies,
  type SortKey,
} from "@/utils/movie";
import type { ContentType } from "@/types/movie";

const PAGE_SIZE = 12;

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "release", label: "最新" },
  { value: "hot", label: "最热" },
  { value: "rating", label: "高分" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const type = (searchParams.get("type") || "all") as ContentType | "all";
  const sort = (searchParams.get("sort") || "release") as SortKey;
  const page = Number(searchParams.get("page") || "1");

  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useDocumentTitle(
    keyword ? `“${keyword}”的搜索结果 - 色色影院` : "搜索影视 - 色色影院",
    "在色色影院搜索电影与电视剧，快速定位你想看的影视内容。",
  );

  const updateParams = (changes: Record<string, string>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (!value || value === "all") next.delete(key);
      else next.set(key, value);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  const matched = useMemo(
    () => filterMovies({ type, keyword }),
    [type, keyword],
  );
  const sorted = useMemo(() => sortMovies(matched, sort), [matched, sort]);
  const { items, totalPages, page: safePage } = paginate(sorted, page, PAGE_SIZE);
  const hotPicks = getMoviesByFlag("isHot", 6);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateParams({ q: input.trim() });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-6 md:py-8">
        <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
            <div className="flex h-12 flex-1 items-center gap-3 rounded-md border border-background-300 bg-background-100 px-4">
              <i className="ri-search-line text-lg text-foreground-500"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、导演或演员"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-search-2-line text-lg"></i>
              搜索
            </button>
          </form>

          {keyword && (
            <p className="mt-4 text-sm text-foreground-600">
              关键词：
              <span className="ml-1 font-semibold text-accent-600">{keyword}</span>
              <span className="ml-3 text-foreground-500">
                找到 <span className="font-semibold text-primary-700">{sorted.length}</span> 个结果
              </span>
            </p>
          )}
        </section>

        {keyword ? (
          <>
            <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "all", label: "全部" },
                  { value: "movie", label: "电影" },
                  { value: "tv", label: "电视剧" },
                ].map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => updateParams({ type: tab.value })}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      type === tab.value
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                <div className="flex rounded-full bg-background-100 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateParams({ sort: option.value })}
                      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        sort === option.value
                          ? "bg-background-50 text-primary-700"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {items.length > 0 ? (
              <>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
                  {items.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination
                  page={safePage}
                  totalPages={totalPages}
                  onChange={(next) => updateParams({ page: String(next) }, false)}
                />
              </>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <h2 className="mt-4 text-lg font-semibold text-foreground-900">
                  没有找到与“{keyword}”相关的内容
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-500">
                  换个关键词试试，或者从下方热门推荐中挑选一部感兴趣的作品。
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h2 className="mt-4 text-lg font-semibold text-foreground-900">开始搜索影视内容</h2>
            <p className="mt-2 text-sm text-foreground-500">
              输入影视名称、导演或演员，即可找到相关作品。
            </p>
          </div>
        )}

        <section className="mt-12">
          <SectionHeading title="热门影视推荐" subtitle="也许这里就有你想找的" moreTo="/category?sort=hot" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {hotPicks.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}